// src/components/TeamManagement.tsx
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
// Removed Accordion imports
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import TeamAlerts from './team/TeamAlerts';
import PlayerTable from './team/PlayerTable';
import { AddPlayerDialog } from './team/AddPlayerDialog';
import AddClubPlayerDialog from './team/AddClubPlayerDialog';
import { Player } from './team/types';

interface Team {
  id: string;
  name: string;
  club_id?: string;
  club_logo?: string;
}

interface TeamManagementProps {
  isAdmin: boolean;
  selectedTeam: Team | null;
}

// Utility to slugify team names
const slugify = (name: string) =>
  name.toLowerCase().replace(/\./g, '').replace(/\s+/g, '-');

const TeamManagement: React.FC<TeamManagementProps> = ({ isAdmin, selectedTeam }) => {
  const { user, userRole, userClubId } = useAuth();
  const { toast } = useToast();
  const playersRef = useRef<HTMLDivElement | null>(null);

  const team = selectedTeam;

  const [players, setPlayers] = useState<Player[]>([]);
  const [otherPlayers, setOtherPlayers] = useState<Player[]>([]);
  const [isAddPlayerOpen, setIsAddPlayerOpen] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerWhsId, setNewPlayerWhsId] = useState('');
  const [isNominationsLocked, setIsNominationsLocked] = useState(false);
  const [addPlayerError, setAddPlayerError] = useState<string | null>(null);
  const [hasFirstGamePlayed, setHasFirstGamePlayed] = useState(false);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [showLockInfo, setShowLockInfo] = useState(false);
  // New state for club player dialog
  const [isAddClubPlayerOpen, setIsAddClubPlayerOpen] = useState(false);
  const [newClubPlayerName, setNewClubPlayerName] = useState('');
  const [newClubPlayerWhsId, setNewClubPlayerWhsId] = useState('');
  const [addClubPlayerError, setAddClubPlayerError] = useState<string | null>(null);
  // State for club name
  const [clubName, setClubName] = useState<string | null>(null);
  // Fetch club name for use in "Other Players" heading
  useEffect(() => {
    if (!team?.club_id) {
      setClubName(null);
      return;
    }

    supabase
      .from('clubs')
      .select('name')
      .eq('id', team.club_id)
      .single()
      .then(({ data, error }) => {
        if (!error && data?.name) {
          setClubName(data.name);
        }
      });
  }, [team?.club_id]);
  const handleAddClubPlayer = async () => {
    const name = newClubPlayerName.trim();
    const whsId = newClubPlayerWhsId.trim();
    if (!name || whsId.length !== 10) {
      return toast({
        title: 'Invalid Input',
        description: 'Name can’t be empty and WHS ID must be 10 digits.',
        variant: 'destructive',
      });
    }

    const { data: existing, error: checkError } = await supabase
      .from('players')
      .select('id')
      .eq('whsId', whsId);
    if (checkError) {
      return toast({
        title: 'Error checking WHS ID',
        description: checkError.message,
        variant: 'destructive',
      });
    }
    if ((existing ?? []).length) {
      setAddClubPlayerError('This player already exists for this club.');
      return;
    }

    const { error: insertErr } = await supabase
      .from('players')
      .insert([{ name, whsId, nominated: false, team_id: null, club_id: team?.club_id }]);
    if (insertErr) {
      return toast({
        title: 'Error adding player',
        description: insertErr.message,
        variant: 'destructive',
      });
    }

    setNewClubPlayerName('');
    setNewClubPlayerWhsId('');
    setIsAddClubPlayerOpen(false);
    setAddClubPlayerError(null);
    toast({
      title: 'Club Player Added',
      description: `${name} has been added.`,
    });
  };

  const isCaptain = userRole === 'captain';
  const canEditPlayers =
    isAdmin || (isCaptain && team?.club_id && userClubId && team.club_id === userClubId);
  const canEditNominations =
    (isAdmin || (!isNominationsLocked && canEditPlayers));

  const nominatedCount = players.filter((p) => p.nominated).length;
  const hasCorrectNominations = nominatedCount === 5;

  const handleLockNominations = async () => {
    if (!team?.id || !hasCorrectNominations) return;

    const { data: userInfo, error: userInfoError } = await supabase
      .from('users')
      .select('name')
      .eq('id', user?.id)
      .single();

    const lockedByName = userInfo?.name || null;

    const { error } = await supabase
      .from('teams')
      .update({
        nominations_locked: true,
        nominations_locked_by: user?.email || null,
        nominations_locked_by_name: lockedByName,
        nominations_locked_at: new Date().toISOString()
      })
      .eq('id', team.id);

    if (error) {
      return toast({
        title: 'Error locking nominations',
        description: error.message,
        variant: 'destructive',
      });
    }

    setIsNominationsLocked(true);
    // Added state update here to trigger re-render for button label
    setIsNominationsLocked(true);
    toast({
      title: 'Nominations Locked',
      description: 'Player nominations have been locked for this team.',
    });
  };

  useEffect(() => {
    if (!team?.id) {
      setPlayers([]);
      return;
    }

    supabase
      .from('players')
      .select('*')
      .eq('team_id', team.id)
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching players:', error.message);
          setPlayers([]);
        } else {
          const sorted = (data ?? []).sort((a, b) => {
            const aWinRate = a.win_percentage ?? 0;
            const bWinRate = b.win_percentage ?? 0;
            if (aWinRate !== bWinRate) return bWinRate - aWinRate;

            const aSecondName = a.name.split(' ')[1] || '';
            const bSecondName = b.name.split(' ')[1] || '';
            return aSecondName.localeCompare(bSecondName);
          });
          setPlayers(sorted);
        }
      });
  }, [team?.id]);

  useEffect(() => {
    if (!team?.club_id) {
      setOtherPlayers([]);
      return;
    }

    supabase
      .from('players')
      .select(`
        *,
        team:teams!inner(id, club_id, is_lowest_team)
      `)
      .eq('team.club_id', team.club_id)
      .neq('team.id', team.id)
      .then(({ data, error }) => {
        if (!error && data) {
          const filtered = (data as any[]).filter((p) =>
            !p.nominated &&
            (!p.team.is_lowest_team || (p.games_played ?? 0) <= 3)
          );
          setOtherPlayers(filtered);
        }
      });
  }, [team?.club_id]);

  useEffect(() => {
    if (!team?.id) return;

    // Check if a fixture has been played
    supabase
      .from('fixtures')
      .select('id')
      .or(`home_team_id.eq.${team.id},away_team_id.eq.${team.id}`)
      .not('submitted_at', 'is', null)
      .limit(1)
      .then(({ data, error }) => {
        if (!error) {
          setHasFirstGamePlayed((data ?? []).length > 0);
        }
      });

    // Get current nominations_locked state and lock info
    supabase
      .from('teams')
      .select('nominations_locked, nominations_locked_by_name, nominations_locked_at')
      .eq('id', team.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setIsNominationsLocked(data.nominations_locked);
          setLockedBy(data.nominations_locked_by_name);
          setLockedAt(data.nominations_locked_at);
        }
      });
  }, [team?.id]);

  const handleAddPlayer = async () => {
    const name = newPlayerName.trim();
    const whsId = newPlayerWhsId.trim();
    if (!name || whsId.length !== 10) {
      return toast({
        title: 'Invalid Input',
        description: 'Name can’t be empty and WHS ID must be 10 digits.',
        variant: 'destructive',
      });
    }

    const { data: existing, error: checkError } = await supabase
      .from('players')
      .select('id')
      .eq('whsId', whsId);
    if (checkError) {
      return toast({
        title: 'Error checking WHS ID',
        description: checkError.message,
        variant: 'destructive',
      });
    }
    if ((existing ?? []).length) {
      setAddPlayerError('This player already exists for this club within a team.');
      return;
    }

    const { error: insertErr } = await supabase
      .from('players')
      .insert([{ name, whsId, team_id: team.id, nominated: false }]);
    if (insertErr) {
      return toast({
        title: 'Error adding player',
        description: insertErr.message,
        variant: 'destructive',
      });
    }

    const { data: updated, error: fetchErr } = await supabase
      .from('players')
      .select('*')
      .eq('team_id', team.id);
    if (!fetchErr && updated) {
      const sorted = updated.sort((a, b) => {
        const aWinRate = a.win_percentage ?? 0;
        const bWinRate = b.win_percentage ?? 0;
        if (aWinRate !== bWinRate) return bWinRate - aWinRate;

        const aSecondName = a.name.split(' ')[1] || '';
        const bSecondName = b.name.split(' ')[1] || '';
        return aSecondName.localeCompare(bSecondName);
      });
      setPlayers(sorted);
    }

    setNewPlayerName('');
    setNewPlayerWhsId('');
    setIsAddPlayerOpen(false);
    setAddPlayerError(null);
    toast({
      title: 'Player Added',
      description: `${name} has been added.`,
    });
  };

  const handleToggleNomination = async (playerId: number) => {
    if (!canEditNominations) {
      return toast({
        title: 'Cannot modify nominations',
        description: 'Nominations are locked after first submitted fixture.',
        variant: 'destructive',
      });
    }

    const player = players.find((p) => p.id === playerId);
    if (!player) return;
    if (!player.nominated && nominatedCount >= 5) {
      return toast({
        title: 'Nomination limit reached',
        description: 'You can only nominate 5 players per team.',
        variant: 'destructive',
      });
    }

    const updated = !player.nominated;
    const { error } = await supabase
      .from('players')
      .update({ nominated: updated })
      .eq('id', playerId);
    if (error) {
      return toast({
        title: 'Error saving nomination',
        description: error.message,
        variant: 'destructive',
      });
    }

    setPlayers((prev) =>
      prev
        .map((p) => (p.id === playerId ? { ...p, nominated: updated } : p))
        .sort((a, b) => {
          if (a.nominated !== b.nominated) return b.nominated ? 1 : -1;
          return a.name.localeCompare(b.name);
        })
    );

    toast({
      title: 'Nomination updated',
      description: `${player.name} has been ${updated ? 'nominated' : 'removed'}.`,
    });
  };

  const handleDeletePlayer = async (playerId: number) => {
    const { error } = await supabase.from('players').delete().eq('id', playerId);
    if (error) {
      return toast({
        title: 'Error deleting player',
        description: error.message,
        variant: 'destructive',
      });
    }
    setPlayers((prev) => prev.filter((p) => p.id !== playerId));
    toast({
      title: 'Player removed',
      description: 'The player has been deleted.',
    });
  };

  if (!team) {
    return <p className="text-muted-foreground px-4">No team selected.</p>;
  }
  return (
    <div className="space-y-6">
      <div>
        <div>
          <CardHeader className="bg-muted/50 pb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {team?.club_logo && (
                  <img
                    src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${team.club_logo}`}
                    alt={`${team.name} Logo`}
                    className="h-8 w-8 object-contain"
                  />
                )}
                <h2 className="text-xl font-medium">
                  Team Players
                </h2>
              </div>
            </div>
          </CardHeader>
        </div>
        <div ref={playersRef}>
          <TeamAlerts
            isAdmin={isAdmin}
            canEditNominations={canEditNominations}
            canEditPlayers={canEditPlayers}
            hasCorrectNominations={hasCorrectNominations}
            nominatedCount={nominatedCount}
          />

          {canEditPlayers && (
            <div className="mb-4">
              {isNominationsLocked ? (
                isAdmin ? (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={async () => {
                      if (!team?.id) return;
                      const { error } = await supabase
                        .from('teams')
                        .update({ nominations_locked: false })
                        .eq('id', team.id);

                      if (error) {
                        return toast({
                          title: 'Error unlocking nominations',
                          description: error.message,
                          variant: 'destructive',
                        });
                      }

                      setIsNominationsLocked(false);
                      toast({
                        title: 'Nominations Unlocked',
                        description: 'Player nominations have been unlocked for this team.',
                      });
                    }}
                  >
                    Unlock Nominations
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled>
                      Nominations Locked
                    </Button>
                    {lockedBy && lockedAt && (
                      <div className="relative group">
                        <svg
                          className="w-4 h-4 text-muted-foreground cursor-pointer"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                        </svg>
                        <div className="absolute bottom-full mb-1 hidden group-hover:block bg-white border rounded px-2 py-1 text-xs text-muted-foreground shadow">
                          Locked by {lockedBy} at {new Date(lockedAt).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                hasCorrectNominations && (
                  <Button
                    size="sm"
                    className="bg-[#30702F] hover:bg-[#256021]"
                    onClick={handleLockNominations}
                  >
                    Lock Nominations
                  </Button>
                )
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <div
              className={`overflow-y-auto rounded-md border max-h-[calc(3rem+${players.length > 8 ? '24rem' : `${players.length * 3}rem`})]`}
            >
              <PlayerTable
                players={players}
                teamName={team?.name || ''}
                canEditPlayers={canEditPlayers}
                canEditNominations={canEditNominations}
                isOwnTeam={false}
                isAdmin={isAdmin}
                onEditClick={() => {}}
                onDeletePlayer={handleDeletePlayer}
                onToggleNomination={handleToggleNomination}
              />
            </div>
          </div>
          {canEditPlayers && (
            <div className="mt-4 flex gap-3">
              <Button size="sm" className="bg-[#30702F] hover:bg-[#256021]" onClick={() => setIsAddPlayerOpen(true)}>
                Add Team Player
              </Button>
            </div>
          )}
        </div>
      </div>
      {team?.club_id && otherPlayers.length > 0 && (
        <div>
          <div>
            <CardHeader className="bg-muted/50 pb-3">
              <div className="flex items-center gap-3">
                {team?.club_logo && (
                  <img
                    src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${team.club_logo}`}
                    alt={`${clubName || 'Club'} Logo`}
                    className="h-8 w-8 object-contain"
                  />
                )}
                <h3 className="text-lg font-medium">
                  Other Players
                </h3>
              </div>
            </CardHeader>
          </div>
          <div>
            <div className="overflow-x-auto">
              <div className="relative">
                <div className="rounded-md border">
                  <PlayerTable
                    players={otherPlayers}
                    teamName={`${team.name} – Other Players`}
                    canEditPlayers={canEditPlayers}
                    canEditNominations={false}
                    isOwnTeam={false}
                    isAdmin={isAdmin}
                    onEditClick={() => {}}
                    onDeletePlayer={handleDeletePlayer}
                    stickyHeader={true}
                  />
                </div>
              </div>
            </div>
            {canEditPlayers && (
              <div className="flex gap-3 mt-4">
                <Button size="sm" className="bg-[#30702F] hover:bg-[#256021]" onClick={() => setIsAddClubPlayerOpen(true)}>
                  Add Club Player
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <AddPlayerDialog
        isOpen={isAddPlayerOpen}
        onOpenChange={setIsAddPlayerOpen}
        playerName={newPlayerName}
        onPlayerNameChange={setNewPlayerName}
        teamName={team?.name || ''}
        whsId={newPlayerWhsId}
        onWhsIdChange={setNewPlayerWhsId}
        onAddPlayer={handleAddPlayer}
        errorMessage={addPlayerError}
      />
      <AddClubPlayerDialog
        isOpen={isAddClubPlayerOpen}
        onOpenChange={setIsAddClubPlayerOpen}
        playerName={newClubPlayerName}
        onPlayerNameChange={setNewClubPlayerName}
        whsId={newClubPlayerWhsId}
        onWhsIdChange={setNewClubPlayerWhsId}
        onAddPlayer={handleAddClubPlayer}
        errorMessage={addClubPlayerError}
        clubName={clubName}
      />
    </div>
  );
};

export default TeamManagement;
